import { useCallback, useEffect, useState, type SetStateAction } from "react";
import { supabase } from "../lib/supabase";
import { categorizeProduct } from "../lib/categorizeProduct";
import type { ProductImage } from "../components/VariantForm";
import { buildThumbnailBlob } from "../utils/thumbnail";
import { recortarAlTope } from "../lib/limiteImagenesProducto";
import { MAX_LARGO_SKU } from "../lib/detallesDelProducto";

/** `sku` es opcional: "Añadir SKU" lo muestra (ADR-2026-10-22-el-alta-tiene-anadir-sku). */
type Borrador = { nombre: string; images: ProductImage[]; sku?: string };

/**
 * Lo cargado y todavía sin guardar, por empresa y marca, mientras dure la pestaña. Ir a
 * "Ver todos los consejos" desde la pantalla de un solo producto desmonta la pantalla; sin
 * esto, al volver se perdían las fotos y el nombre
 * (ADR-2026-10-13-un-solo-producto-reestructurado). Vive en memoria y no en
 * `localStorage`: las fotos son `File` con URLs `blob:` que sólo valen en esta pestaña.
 */
const borradores = new Map<string, Borrador>();
const claveDeBorrador = (companyId: string | null, brandId: string | null) =>
  companyId ? `${companyId}:${brandId ?? "-"}` : null;
const VACIO: Borrador = { nombre: "", images: [] };
const esVacio = (b: Borrador) => b.images.length === 0 && !b.nombre;

/**
 * Qué mostrar cuando cambia la clave del borrador (empresa:marca). La empresa y la marca
 * se resuelven después del primer render: pasar de "sin empresa" o de "sin marca" a la
 * clave real no es cambiar de marca, y lo cargado se queda (y se suelta la clave vieja).
 * Cambiar de marca de verdad muestra el borrador de la nueva o nada: lo de la anterior
 * sigue guardado bajo SU clave.
 */
export function alCambiarDeClave(
  anterior: string | null,
  clave: string | null,
  actual: Borrador,
  leer: (clave: string) => Borrador | undefined,
): { producto: Borrador; soltar: string | null } {
  const resolviendo =
    !anterior || (clave !== null && anterior === `${clave.split(":")[0]}:-`);
  if (resolviendo && !esVacio(actual)) return { producto: actual, soltar: anterior };
  return { producto: (clave && leer(clave)) || VACIO, soltar: null };
}

/**
 * El alta de UN producto: sus fotos, su nombre y el guardado (producto + variante +
 * imágenes full y thumbnail + categorización IA). Antes vivía adentro de
 * `AddProductFlow`, que montaba el panel lateral; desde que subir un producto es una
 * pantalla de Mi marca (ADR-2026-10-13-subir-productos-es-una-pantalla) la usa
 * `SubirProductos`, y la lógica se sacó acá para no copiarla.
 *
 * También trae el rubro (de la marca activa, con el de la empresa de respaldo) y el plan
 * de la empresa: el rubro elige los ejemplos de foto y el plan, el tope de la subida en
 * bloque.
 */
export const useAltaDeProducto = ({
  companyId,
  brandId,
}: {
  companyId: string | null;
  brandId: string | null;
}) => {
  const clave = claveDeBorrador(companyId, brandId);
  // El borrador viaja CON la clave a la que pertenece. Cuando la clave cambia se ajusta
  // durante el render (no en un efecto): así el efecto que guarda nunca ve el producto de
  // una marca con la clave de otra, y lo de la marca A no se escribe ni se crea en la B
  // (review Greptile #184).
  const [estado, setEstado] = useState<{ clave: string | null; producto: Borrador }>(
    () => ({ clave, producto: (clave && borradores.get(clave)) || VACIO }),
  );
  if (estado.clave !== clave) {
    const { producto: siguiente, soltar } = alCambiarDeClave(
      estado.clave,
      clave,
      estado.producto,
      (k) => borradores.get(k),
    );
    if (soltar) borradores.delete(soltar);
    setEstado({ clave, producto: siguiente });
  }
  const producto = estado.clave === clave ? estado.producto : VACIO;
  const setProducto = useCallback((u: SetStateAction<Borrador>) => {
    setEstado((e) => ({
      ...e,
      producto: typeof u === "function" ? u(e.producto) : u,
    }));
  }, []);
  useEffect(() => {
    if (!estado.clave) return;
    if (esVacio(estado.producto)) borradores.delete(estado.clave);
    else borradores.set(estado.clave, estado.producto);
  }, [estado]);
  /** Tira lo cargado (al cancelar). */
  const descartar = useCallback(() => {
    if (clave) borradores.delete(clave);
    setProducto(VACIO);
  }, [clave, setProducto]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rubro, setRubro] = useState<string>("Moda e Indumentaria");
  const [plan, setPlan] = useState<string>("gratis");

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: company } = await supabase
          .from("company_profiles")
          .select("plan, rubro")
          .eq("id_company", companyId)
          .single();
        if (!cancelled && company?.plan) setPlan(company.plan as string);
        if (!cancelled && (company as any)?.rubro && !brandId)
          setRubro((company as any).rubro as string);
        if (brandId) {
          const { data: brand } = await supabase
            .from("brand_profiles")
            .select("rubro")
            .eq("id", brandId)
            .single();
          if (!cancelled && (brand as any)?.rubro)
            setRubro((brand as any).rubro as string);
        }
      } catch {
        /* noop — valores por defecto */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, brandId]);

  /**
   * Crea el producto. Devuelve el id de la VARIANTE creada (truthy) o `null` si no se guardó.
   * El id lo usa quien tiene que completar la ficha después de crear (la carga desde un PDF:
   * ADR-2026-10-18-el-producto-se-carga-desde-el-pdf-del-proveedor).
   */
  const guardar = useCallback(async (): Promise<string | null> => {
    if (producto.images.length === 0) return null;
    try {
      setGuardando(true);
      setError(null);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");
      if (!companyId) throw new Error("No company associated with user");
      const idCompany = companyId;

      // El SKU es único por empresa (`idx_variantes_unique_sku_per_company`). Se mira ANTES de
      // crear nada: si chocara al insertar la variante, el producto ya estaría creado.
      const sku = (producto.sku ?? "").trim().slice(0, MAX_LARGO_SKU) || null;
      if (sku) {
        const { data: conEseSku } = await supabase
          .from("variantes")
          .select("nombre")
          .eq("id_company", idCompany)
          .eq("sku", sku)
          .maybeSingle();
        if (conEseSku) {
          const otro = (conEseSku as { nombre?: string | null }).nombre?.trim();
          throw new Error(
            otro
              ? `El SKU ${sku} ya lo tiene "${otro}". Usá otro código.`
              : `El SKU ${sku} ya lo tiene otro producto. Usá otro código.`,
          );
        }
      }

      // Nombre placeholder si quedó vacío (el backend lo completa con IA).
      let effectiveName = producto.nombre.trim();
      if (!effectiveName) {
        const { count } = await supabase
          .from("productos")
          .select("id", { count: "exact", head: true })
          .eq("id_company", idCompany);
        effectiveName = `Producto ${(count ?? 0) + 1}`;
      }

      // 1) Crear producto (asociado a la marca activa si hay una)
      const productoPayload: Record<string, any> = {
        id_company: idCompany,
        nombre: effectiveName,
        tiene_variantes: false,
      };
      if (brandId) productoPayload.brand_id = brandId;
      const { data: creado, error: productoError } = await supabase
        .from("productos")
        .insert(productoPayload)
        .select("id")
        .single();
      if (productoError) throw productoError;
      const productId = String(creado.id);

      // 2) Crear variante
      const { data: variant, error: variantError } = await supabase
        .from("variantes")
        .insert({
          id_company: idCompany,
          nombre: effectiveName,
          activo: true,
          product_id: productId,
          ...(sku ? { sku } : {}),
        })
        .select("id")
        .single();
      if (variantError) {
        // Sin variante el producto no se ve en ningún lado: no se deja huérfano (pasa si otro
        // guardado tomó el mismo SKU entre la consulta de arriba y este insert).
        await supabase.from("productos").delete().eq("id", productId).eq("id_company", idCompany);
        if (variantError.code === "23505" && sku) {
          throw new Error(`El SKU ${sku} ya lo tiene otro producto. Usá otro código.`);
        }
        throw variantError;
      }
      const variantId = variant.id as string;

      // 3) Subir imágenes (full + thumbnail opt) e insertar filas en DB
      // El picker (TempImageManager) ya recorta al tope y avisa; este slice es la red
      // de contención del write path.
      const imagenesAlta = recortarAlTope(0, producto.images).aceptadas;
      for (let i = 0; i < imagenesAlta.length; i++) {
        const imgData = imagenesAlta[i];
        if (!imgData.tempFile) continue;

        const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
        if (imgData.tempFile.size > MAX_FILE_SIZE) {
          throw new Error(
            "El archivo supera el límite de 100 MB. Reducí el tamaño e intentá de nuevo.",
          );
        }

        const timestamp = Date.now();
        const originalName = imgData.fileName || imgData.tempFile.name;
        const sanitizedName = originalName.replace(/[^a-zA-Z0-9._-]/g, "_");
        const fileBaseName = `${timestamp}_${sanitizedName}`;

        const fullPath = `${idCompany}/${variantId}/full/${fileBaseName}`;
        const { error: fullErr } = await supabase.storage
          .from("productos")
          .upload(fullPath, imgData.tempFile);
        if (fullErr) throw fullErr;

        // Thumbnail optimizado vía canvas (best-effort).
        try {
          const blob = await buildThumbnailBlob(imgData.tempFile as File);
          if (!blob) throw new Error("no se pudo generar el thumbnail");
          const optPath = `${idCompany}/${variantId}/opt/${fileBaseName}`;
          const { error: optErr } = await supabase.storage
            .from("productos")
            .upload(optPath, blob);
          if (optErr)
            console.warn("Optimized upload failed, continuing", optErr);
        } catch (optErr) {
          console.warn("Error creating optimized image, continuing", optErr);
        }

        const { error: imgInsertError } = await supabase
          .from("productos_imagenes")
          .insert({
            id_company: idCompany,
            variante_id: variantId,
            url: fileBaseName,
            principal: imgData.principal || i === 0,
            orden: imgData.orden || i + 1,
          });
        if (imgInsertError) throw imgInsertError;
      }

      // Categorización IA (best-effort, fire-and-forget)
      categorizeProduct(String(productId), {
        modo: "crear",
        completarNombre: !producto.nombre.trim(),
        userId: user.id,
      });

      setProducto({ nombre: "", images: [] });
      return variantId;
    } catch (err) {
      console.error("Error saving product:", err);
      setError(
        err instanceof Error ? err.message : "Error al guardar producto",
      );
      return null;
    } finally {
      setGuardando(false);
    }
  }, [producto, companyId, brandId]);

  return { producto, setProducto, guardando, error, guardar, descartar, rubro, plan };
};
